"use client"

import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { Typography } from "@mui/material"

/**
 * Renders a TinyMCE rich text value from Umbraco.
 *
 * The CMS output is plain HTML with inline `text-align` styles and no classes, so every
 * element it can emit needs styling here: paragraphs, bold runs, in-page anchors, links,
 * bullet lists and — on the accessibility statement — a table.
 */
const StyledBody = styled(Typography)<{ component: string }>`
  font-size: 18px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    font-size: 20px;
  }

  /* :first-child, not :first-of-type — the latter matches the first element of every type,
     which would strip the top margin off the first h3, table and so on mid-document. */
  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  /* 32px between blocks matches the design's rhythm — measured against the Figma article
     page, whose body lines sit on a 28px pitch with ~32px between blocks. */
  p {
    margin: 0 0 32px;
  }

  strong,
  b {
    font-weight: 600;
  }

  /* Section headings inside the body sit just above body size, per the design —
     not the theme h2/h3 scale, which is reserved for the page title. */
  h2,
  h3,
  h4 {
    font-weight: 600;
    line-height: 140%;
    margin: 32px 0 8px;
  }

  h2 {
    font-size: 20px;

    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 22px;
    }
  }

  /* The privacy policy nests numbered sub-sections (2.1 under 2) — keep them a step down
     from h2 so the hierarchy still reads. */
  h3,
  h4 {
    font-size: 18px;

    ${({ theme }) => theme.breakpoints.up("md")} {
      font-size: 20px;
    }
  }

  /* The privacy policy links to its own sections (#sec-1 …) — keep the target clear
     of the fixed header. */
  [id] {
    scroll-margin-top: 96px;
  }

  a {
    color: ${sharedColors.darkGreen};
    text-decoration: underline;
    overflow-wrap: anywhere;
  }

  ul,
  ol {
    margin: 0 0 32px;
    padding-inline-start: 28px;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  /* The design's bullet rows sit on a ~44px pitch against a 28px line height. */
  li {
    margin-bottom: 16px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* No table design was provided (see END-75) — this only keeps the accessibility
     statement's branch table from overflowing the page. */
  table {
    display: block;
    overflow-x: auto;
    max-width: 100%;
    border-collapse: collapse;
    margin: 0 0 32px;
    font-size: 16px;
  }

  th,
  td {
    border: 1px solid ${sharedColors.lightGrey};
    padding: 8px 12px;
    text-align: start;
    vertical-align: top;
  }

  th {
    background-color: ${sharedColors.lightGrey};
    font-weight: 600;
  }
`

export default function RichTextBody({ content }: { content: string }) {
  return (
    <StyledBody
      variant="body1"
      component="div"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}
